<template>
	<div>
		<b-button
			v-ripple.400="'rgba(113, 102, 240, 0.15)'"
			:size="size"
			:block="block"
			:variant="variant"
			@click="openModal"
		>
			<feather-icon
				size="1x"
				:icon="displayIcon"
			/>
			{{ displayText }}
		</b-button>

		<b-modal
			ref="modal"
			centered
			size="xl"
			title="EVIDENCIA DEL PROVEEDOR"
			modal-class="modal-primary"
			no-close-on-backdrop
			no-close-on-esc
			@hidden="onModalHidden"
		>
			<!-- Countdown for provider -->
			<b-alert
				v-if="isProvider && !expired && !submitted"
				show
				variant="info"
				class="p-1 text-center mb-2"
			>
				<feather-icon
					icon="ClockIcon"
					class="mr-1"
				/>
				<strong>Tiempo restante: {{ countdownText }}</strong>
			</b-alert>

			<b-alert
				v-if="isProvider && expired && !submitted"
				show
				variant="danger"
				class="p-1 text-center mb-2"
			>
				<feather-icon
					icon="AlertOctagonIcon"
					class="mr-1"
				/>
				<strong>Tiempo agotado</strong> - Ya no puede cargar archivos ni enviar la solicitud.
			</b-alert>

			<b-alert
				v-if="isProvider && submitted"
				show
				variant="success"
				class="p-1 text-center mb-2"
			>
				<feather-icon
					icon="CheckCircleIcon"
					class="mr-1"
				/>
				<strong>Solicitud enviada</strong> - Su solicitud se encuentra en revisi&oacute;n.
			</b-alert>

			<!-- Evidence type selection -->
			<div class="my-2">
				<label class="font-weight-bold text-primary">Tipo de solicitud</label>
				<b-form-radio-group
					v-model="evidenceType"
					:options="computedEvidenceTypeOptions"
					name="evidence-type"
					class="mt-1"
					@change="onEvidenceTypeChange"
					:disabled="isProvider || !canEdit || submitted"
				></b-form-radio-group>
			</div>

			<b-alert
				show
				variant="info"
			>
				<div
					class="alert-body"
					style="font-size: 0.85rem;"
				>
					<ul class="mb-0">
						<li>
							<strong>Mensual:</strong> Aplica para
							<strong>todos los meses</strong>.
							<b-badge
								v-if="isMensualAllowed"
								variant="light-success"
								class="ml-1"
							>Activo</b-badge>
							<b-badge
								v-if="!isMensualAllowed"
								variant="light-danger"
								class="ml-1"
							>Bloqueado</b-badge>
						</li>
						<li>
							<strong>CTS:</strong> Aplica para los meses de
							<strong>Abril</strong> y
							<strong>Octubre</strong>.
							<b-badge
								v-if="isCtsAllowed"
								variant="light-success"
								class="ml-1"
							>Activo</b-badge>
							<b-badge
								v-if="!isCtsAllowed"
								variant="light-danger"
								class="ml-1"
							>Bloqueado</b-badge>
						</li>
						<li>
							<strong>Gratificaci&oacute;n:</strong> Aplica para los meses de
							<strong>Junio</strong> y
							<strong>Noviembre</strong>.
							<b-badge
								v-if="isGratificationAllowed"
								variant="light-success"
								class="ml-1"
							>Activo</b-badge>
							<b-badge
								v-if="!isGratificationAllowed"
								variant="light-danger"
								class="ml-1"
							>Bloqueado</b-badge>
						</li>
					</ul>
				</div>
			</b-alert>

			<template v-if="evidenceType">
				<hr />

				<div class="my-2">
					<label class="font-weight-bold text-primary">Documentos requeridos</label>

					<table class="table table-bordered w-100 mt-1">
						<thead>
							<tr>
								<th style="width: 40px">#</th>
								<th>Documento</th>
								<th style="width: 450px">Archivo</th>
								<th
									style="width: 180px"
									class="text-center"
								>Opciones</th>
								<th
									v-if="isAdmin"
									style="width: 100px"
									class="text-center"
								>Validación</th>
							</tr>
						</thead>
						<tbody>
							<tr
								v-for="(item, index) in currentItems"
								:key="item.key"
								:class="{ 'bg-light': item.excluded }"
							>
								<td>{{ index + 1 }}</td>
								<td>
									{{ item.label }}
									<b-badge
										v-if="item.excluded"
										variant="light-info"
										class="ml-1"
									>EXONERADO</b-badge>
								</td>
								<td>
									<template v-if="item.excluded">
										<span class="text-info font-italic">No aplica para este proveedor</span>
									</template>
									<template v-else-if="getFileForItem(item.key)">
										<span class="text-success">
											<feather-icon
												icon="CheckCircleIcon"
												class="mr-1"
											/>
											{{ getFileForItem(item.key).file_original_name }}
										</span>
									</template>
									<template v-else-if="canEdit">
										<div class="custom-file">
											<input
												type="file"
												class="custom-file-input"
												:id="'file-' + item.key"
												:accept="getAllowedAccept(index)"
												@change="onFileSelected($event, item.key, index)"
												:disabled="uploading[item.key]"
											/>
											<label
												class="custom-file-label"
												:for="'file-' + item.key"
											>{{ uploading[item.key] ? 'Cargando...' : 'Seleccionar archivo' }}</label>
										</div>
									</template>
									<template v-else>
										<span class="text-muted font-italic">Sin archivo</span>
									</template>
								</td>
								<td class="text-center">
									<template v-if="item.excluded">
										<span class="text-muted">-</span>
									</template>
									<template v-else-if="getFileForItem(item.key)">
										<b-button
											v-ripple.400="'rgba(113, 102, 240, 0.15)'"
											v-b-tooltip.hover.v-info
											variant="flat-info"
											class="btn-sm btn-icon mr-50"
											title="DESCARGAR"
											@click="viewFile(getFileForItem(item.key).id)"
										>
											<feather-icon icon="DownloadIcon" />
										</b-button>

										<b-button
											v-if="canEdit"
											v-ripple.400="'rgba(234, 84, 85, 0.15)'"
											v-b-tooltip.hover.v-danger
											variant="flat-danger"
											class="btn-sm btn-icon"
											title="ELIMINAR"
											@click="deleteFile(getFileForItem(item.key).id)"
										>
											<feather-icon icon="TrashIcon" />
										</b-button>
									</template>
									<template v-else>
										<span class="text-muted">-</span>
									</template>
								</td>
								<td
									v-if="isAdmin"
									class="text-center"
								>
									<template v-if="item.excluded">
										<b-badge variant="light-info">EXONERADO</b-badge>
									</template>
									<template v-else>
										<feather-icon
											:icon="fileValidations[item.key] !== false ? 'CheckCircleIcon' : 'XCircleIcon'"
											:class="fileValidations[item.key] !== false ? 'text-success cursor-pointer' : 'text-danger cursor-pointer'"
											size="20"
											@click="toggleValidation(item.key)"
										/>
									</template>
								</td>
							</tr>
						</tbody>
					</table>
				</div>

				<!-- Admin validation section -->
				<template v-if="isAdmin">
					<hr />

					<div
						v-if="isCtsOrGratificacion && hasUploadedFiles"
						class="mb-2"
					>
						<b-button
							variant="primary"
							size="sm"
							:disabled="downloadingAll"
							@click="downloadAllFiles"
						>
							<b-spinner
								v-if="downloadingAll"
								small
								class="mr-1"
							/>
							<feather-icon
								v-else
								icon="DownloadIcon"
								class="mr-1"
							/>
							Descargar todos los archivos
						</b-button>
					</div>

					<div class="mb-2">
						<template v-if="isCtsOrGratificacion">
							<label class="font-weight-bold text-primary">Comentarios de las {{ totalCartasCount }} cartas no adeudos</label>
							<b-form-textarea
								v-model="evidenceComment"
								rows="2"
								placeholder="Ingrese un comentario sobre la validación"
							/>
						</template>

						<label class="font-weight-bold text-primary mt-1">Observación</label>
						<b-form-textarea
							v-model="evidenceObservation"
							rows="2"
							placeholder="Ingrese una observación"
						/>
					</div>
				</template>

				<!-- Requested amount - editable for provider (not expired, not submitted) -->
				<template v-if="isProvider && !expired && !submitted">
					<hr />

					<div class="mb-2">
						<label
							for="requestedAmount"
							class="font-weight-bold text-primary"
						>Importe a solicitar</label>
						<b-form-input
							type="number"
							id="requestedAmount"
							v-model="requestedAmount"
							placeholder="Ingrese el importe"
							step="0.01"
							min="0"
							@keypress="onlyNumbers"
						></b-form-input>
						<small
							v-if="requestedAmountFormatted"
							class="text-muted"
						>S/ {{ requestedAmountFormatted }}</small>
					</div>
				</template>

				<!-- Editable amount for admin -->
				<template v-if="isAdmin && requestedAmount !== null">
					<hr />
					<div class="mb-2">
						<label class="font-weight-bold text-primary">Importe solicitado</label>
						<b-form-input
							type="number"
							v-model="requestedAmount"
							step="0.01"
							min="0"
						/>
						<small
							v-if="requestedAmountFormatted"
							class="text-muted"
						>S/ {{ requestedAmountFormatted }}</small>
					</div>
				</template>

				<!-- Read-only amount for provider (submitted) -->
				<template v-else-if="isProvider && submitted && requestedAmount">
					<hr />
					<div class="mb-2">
						<label class="font-weight-bold text-primary">Importe solicitado:</label>
						<span class="ml-1">S/ {{ requestedAmountFormatted }}</span>
					</div>
				</template>
			</template>

			<template #modal-footer="{ cancel }">
				<b-button
					variant="outline-secondary"
					@click="cancel()"
				>CERRAR</b-button>
				<b-button
					v-if="isAdmin"
					variant="success"
					@click="saveValidation"
					:disabled="savingValidation"
				>
					<b-spinner
						v-if="savingValidation"
						small
						class="mr-1"
					/>
					{{ savingValidation ? 'GUARDANDO...' : 'GUARDAR VALIDACIÓN' }}
				</b-button>
				<b-button
					v-if="isProvider && !expired && !submitted"
					variant="primary"
					@click="submitEvidence"
					:disabled="submitting"
				>
					<b-spinner
						v-if="submitting"
						small
						class="mr-1"
					/>
					{{ submitting ? 'ENVIANDO...' : 'ENVIAR A REVISION' }}
				</b-button>
			</template>
		</b-modal>
	</div>
</template>

<script>
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue"
import { ref, computed, onUnmounted } from "@vue/composition-api"
import uploadEvidenceStoreModule from "./uploadEvidenceStoreModule"
import { useToast } from "vue-toastification/composition"
import VueSweetalert2 from "vue-sweetalert2"
import { getUserRole, getRoles } from "@/auth/utils"
import Ripple from "vue-ripple-directive"
import store from "@/store"
import moment from "moment"
import axios from "@axios"
import Vue from "vue"
import "animate.css"

import {
	MENSUAL_ID,
	CTS_ID,
	GRATIFICACION_ID,
	CTS_MONTHS,
	GRATIFICACION_MONTHS,
	MENSUAL_MONTHS,
	getRequiredEvidenceType
} from "@core/utils/evidenceTypeSchedule"

Vue.use(VueSweetalert2)

export default {
	props: {
		formId: {
			type: Number,
			required: true
		},
		text: {
			type: String,
			default: "CARGAR ARCHIVOS"
		},
		size: {
			type: String,
			default: "md"
		},
		block: {
			type: Boolean,
			default: false
		},
		variant: {
			type: String,
			default: "outline-primary"
		},
		icon: {
			type: String,
			default: "UploadIcon"
		}
	},
	directives: {
		Ripple
	},
	data() {
		return {
			moment
		}
	},
	setup(props, { emit }) {
		// USE TOAST
		const toast = useToast()

		// USER ROLE
		const { SUPERADMIN, ADMIN, PROVIDER } = getRoles()
		const userRole = getUserRole()

		const isProvider = computed(() => userRole === PROVIDER)
		const isAdmin = computed(
			() => userRole === ADMIN || userRole === SUPERADMIN
		)

		// REFS
		const form = ref(props.formId)
		const modal = ref(null)
		const files = ref([])

		const evidenceType = ref(null)
		const requestedAmount = ref(null)
		const requestedAmountFormatted = computed(() => {
			const n = parseFloat(requestedAmount.value)
			if (isNaN(n)) return null
			return new Intl.NumberFormat("es-PE", {
				minimumFractionDigits: 2,
				maximumFractionDigits: 2
			}).format(n)
		})
		const uploading = ref({})
		const submitting = ref(false)
		const submitted = ref(false)
		const periodMonth = ref(null)

		// Admin validation refs
		const fileValidations = ref({})
		const evidenceComment = ref("")
		const evidenceObservation = ref("")
		const savingValidation = ref(false)
		const downloadingAll = ref(false)
		const hasUploadedFiles = computed(() => {
			const f = files.value
			if (Array.isArray(f)) return f.length > 0
			if (f && typeof f === "object") return Object.keys(f).length > 0
			return false
		})

		const downloadAllFiles = async () => {
			if (downloadingAll.value) return
			downloadingAll.value = true
			try {
				const response = await axios.get(
					`/evidence/${props.formId}/download-all`,
					{ responseType: "blob" }
				)

				let filename = "archivos.zip"
				const cd = response.headers["content-disposition"]
				if (cd) {
					const match = cd.match(/filename="?([^"]+)"?/)
					if (match) filename = match[1]
				}

				const url = window.URL.createObjectURL(new Blob([response.data]))
				const link = document.createElement("a")
				link.href = url
				link.setAttribute("download", filename)
				document.body.appendChild(link)
				link.click()
				link.remove()
				window.URL.revokeObjectURL(url)
			} catch (err) {
				toast({
					component: ToastificationContent,
					props: {
						title: "Error al descargar los archivos.",
						icon: "AlertTriangleIcon",
						variant: "danger"
					}
				})
			} finally {
				downloadingAll.value = false
			}
		}

		// DEADLINE / COUNTDOWN
		const deadline = ref(null)
		const expired = ref(false)
		const countdownText = ref("--:--:--")
		let countdownInterval = null

		// canEdit: provider (not expired and not submitted) or admin
		const canEdit = computed(() => {
			if (isAdmin.value) return true
			if (isProvider.value && !expired.value && !submitted.value) return true
			return false
		})

		// Dynamic button text and icon
		const displayText = computed(() => {
			if (isProvider.value && (expired.value || submitted.value))
				return "VER ARCHIVOS"
			return props.text
		})

		const displayIcon = computed(() => {
			if (isProvider.value && (expired.value || submitted.value))
				return "EyeIcon"
			return props.icon
		})

		const evidenceTypeOptions = ref([])

		const evidenceTypeLabel = computed(() => {
			const opt = evidenceTypeOptions.value.find(
				(o) => o.value === evidenceType.value
			)
			return opt ? opt.text : ""
		})

		const requiredEvidenceType = computed(() => {
			// Trust the form's stored type when set (cron/backfill assigns it
			// correctly at creation). Fall back to month-based inference only
			// for legacy forms where evidence_type_id is still null.
			if (evidenceType.value) return evidenceType.value
			if (periodMonth.value === null) return null
			return getRequiredEvidenceType(periodMonth.value)
		})

		const isMensualAllowed = computed(() => {
			if (periodMonth.value === null) return true
			return requiredEvidenceType.value === MENSUAL_ID
		})

		const isCtsAllowed = computed(() => {
			if (periodMonth.value === null) return true
			return requiredEvidenceType.value === CTS_ID
		})

		const isGratificationAllowed = computed(() => {
			if (periodMonth.value === null) return true
			return requiredEvidenceType.value === GRATIFICACION_ID
		})

		const computedEvidenceTypeOptions = computed(() => {
			return evidenceTypeOptions.value.map((opt) => ({
				...opt,
				disabled:
					requiredEvidenceType.value !== null &&
					opt.value !== requiredEvidenceType.value
			}))
		})

		const evidenceItems = ref([])

		const currentItems = computed(() => {
			const currentType = evidenceTypeOptions.value.find(
				(o) => o.value === evidenceType.value
			)
			const isMensual =
				currentType &&
				currentType.text &&
				currentType.text.toUpperCase().includes("MENSUAL")

			let filtered = evidenceItems.value

			if (isMensual && isProvider.value) {
				filtered = filtered.filter(
					(item) =>
						item.label &&
						item.label.toUpperCase().includes("DOCUMENTO DE SOLICITUD")
				)
			}

			// Provider: hide excluded items; Admin: keep them visible
			if (isProvider.value) {
				filtered = filtered.filter((item) => !item.excluded)
			}

			return filtered
		})

		const isCtsOrGratificacion = computed(() => {
			return [CTS_ID, GRATIFICACION_ID].includes(evidenceType.value)
		})

		const totalCartasCount = computed(() => {
			return evidenceItems.value.filter(
				(item) => item.key && item.key.startsWith("carta_")
			).length
		})

		// Count only cartas actually presented: skip the provider's exonerated
		// carta and cartas without a file (e.g. added after the form was sent)
		const validCartasCount = computed(() => {
			return evidenceItems.value.filter(
				(item) =>
					item.key &&
					item.key.startsWith("carta_") &&
					!item.excluded &&
					files.value.some((f) => f.item_key === item.key) &&
					fileValidations.value[item.key] !== false
			).length
		})

		const autoGenerateComment = () => {
			if (isCtsOrGratificacion.value) {
				evidenceComment.value = `PRESENTÓ ${validCartasCount.value} CARTAS NO ADEUDOS`
			}
		}

		const toggleValidation = (itemKey) => {
			Vue.set(
				fileValidations.value,
				itemKey,
				fileValidations.value[itemKey] === false ? true : false
			)
			if (isCtsOrGratificacion.value) {
				autoGenerateComment()
			}
		}

		const saveValidation = () => {
			savingValidation.value = true

			store
				.dispatch("app-upload-evidence/saveEvidenceValidation", {
					formId: form.value,
					payload: {
						validations: fileValidations.value,
						comment: isCtsOrGratificacion.value ? evidenceComment.value : null,
						observation: evidenceObservation.value,
						evidence_requested_amount: isAdmin.value ? parseFloat(requestedAmount.value) || null : undefined
					}
				})
				.then((response) => {
					emit("refetch-data")

					toast({
						component: ToastificationContent,
						props: {
							title: response.data.message,
							icon: "CheckIcon",
							variant: "success"
						}
					})
				})
				.catch((err) => {
					let message =
						err.response && err.response.data && err.response.data.message
							? err.response.data.message
							: "Error al guardar la validación."

					toast({
						component: ToastificationContent,
						props: {
							title: message,
							icon: "AlertTriangleIcon",
							variant: "danger"
						}
					})
				})
				.finally(() => {
					savingValidation.value = false
				})
		}

		const UPLOAD_EVIDENCE_APP_STORE_MODULE_NAME = "app-upload-evidence"

		// REGISTER MODULE
		if (!store.hasModule(UPLOAD_EVIDENCE_APP_STORE_MODULE_NAME))
			store.registerModule(
				UPLOAD_EVIDENCE_APP_STORE_MODULE_NAME,
				uploadEvidenceStoreModule
			)

		// UNREGISTER ON LEAVE
		onUnmounted(() => {
			if (store.hasModule(UPLOAD_EVIDENCE_APP_STORE_MODULE_NAME))
				store.unregisterModule(UPLOAD_EVIDENCE_APP_STORE_MODULE_NAME)
			stopCountdown()
		})

		// Fetch initial state for button text
		if (isProvider.value) {
			store
				.dispatch("app-upload-evidence/fetchEvidenceFiles", {
					formId: form.value
				})
				.then((response) => {
					expired.value = response.data.expired || false
					submitted.value = response.data.submitted || false
					deadline.value = response.data.deadline
				})
				.catch(() => {})
		}

		// COUNTDOWN FUNCTIONS
		const startCountdown = () => {
			stopCountdown()
			if (!deadline.value) return

			const updateCountdown = () => {
				const now = moment()
				const end = moment(deadline.value)
				const diff = end.diff(now)

				if (diff <= 0) {
					expired.value = true
					countdownText.value = "00:00:00"
					stopCountdown()
					return
				}

				const duration = moment.duration(diff)
				const days = Math.floor(duration.asDays())
				const hours = String(duration.hours()).padStart(2, "0")
				const minutes = String(duration.minutes()).padStart(2, "0")
				const seconds = String(duration.seconds()).padStart(2, "0")

				if (days > 0) {
					countdownText.value = `${days} día${
						days > 1 ? "s" : ""
					} ${hours}:${minutes}:${seconds}`
				} else {
					countdownText.value = `${hours}:${minutes}:${seconds}`
				}
			}

			updateCountdown()
			countdownInterval = setInterval(updateCountdown, 1000)
		}

		const stopCountdown = () => {
			if (countdownInterval) {
				clearInterval(countdownInterval)
				countdownInterval = null
			}
		}

		const onModalHidden = () => {
			stopCountdown()
		}

		const getFileForItem = (itemKey) => {
			return files.value.find((f) => f.item_key === itemKey)
		}

		const openModal = () => {
			Promise.all([
				store.dispatch("app-upload-evidence/fetchEvidenceFiles", {
					formId: form.value
				}),
				store.dispatch("app-upload-evidence/fetchEvidenceTypes")
			]).then(([evidenceResponse, typesResponse]) => {
				files.value = evidenceResponse.data.files
				evidenceType.value = evidenceResponse.data.evidence_type_id
				requestedAmount.value = evidenceResponse.data.evidence_requested_amount
				evidenceItems.value = evidenceResponse.data.items || []
				deadline.value = evidenceResponse.data.deadline
				expired.value = evidenceResponse.data.expired || false
				submitted.value = evidenceResponse.data.submitted || false

				// Use period_month from backend response
				periodMonth.value = evidenceResponse.data.period_month || null

				// Load validation data for admin
				if (evidenceResponse.data.evidence_validations) {
					fileValidations.value = evidenceResponse.data.evidence_validations
				} else {
					// Initialize all items as valid (true)
					const initValidations = {}
					const allItems = evidenceResponse.data.items || []
					allItems.forEach((item) => {
						initValidations[item.key] = true
					})
					fileValidations.value = initValidations
				}
				evidenceComment.value = evidenceResponse.data.evidence_comment || ""
				evidenceObservation.value = evidenceResponse.data.evidence_observation || ""
				if (!evidenceResponse.data.evidence_comment && isAdmin.value) {
					autoGenerateComment()
				}

				evidenceTypeOptions.value = typesResponse.data.types || []

				// If the form already has an evidence_type_id (set at creation
				// time by the cron / backfill), trust it. Only fall back to the
				// month-based default for legacy forms where it's still null.
				if (isProvider.value && !evidenceType.value && periodMonth.value !== null) {
					const requiredType = getRequiredEvidenceType(periodMonth.value)
					evidenceType.value = requiredType
					onEvidenceTypeChange(requiredType)
				} else if (!evidenceType.value && evidenceTypeOptions.value.length) {
					// Default to first allowed option if no type is set (admin fallback)
					evidenceType.value = evidenceTypeOptions.value[0].value
					onEvidenceTypeChange(evidenceType.value)
				}

				if (isProvider.value && !expired.value && !submitted.value) {
					startCountdown()
				}

				modal.value.show()
			})
		}

		const getFiles = () => {
			store
				.dispatch("app-upload-evidence/fetchEvidenceFiles", {
					formId: form.value
				})
				.then((response) => {
					files.value = response.data.files
				})
		}

		const onEvidenceTypeChange = (newType) => {
			// Fetch items for the new evidence type (pass formId for provider exclusions)
			store
				.dispatch("app-upload-evidence/fetchEvidenceItems", {
					evidenceTypeId: newType,
					formId: form.value
				})
				.then((response) => {
					evidenceItems.value = response.data.items || []
				})
			getFiles()
		}

		const viewFile = (fileId) => {
			store
				.dispatch("app-upload-evidence/viewEvidenceFile", {
					formId: form.value,
					fileId
				})
				.then((response) => {
					window.open(response.data.url, "_blank")
				})
				.catch(() => {
					toast({
						component: ToastificationContent,
						props: {
							title: "Error al descargar el archivo",
							icon: "AlertTriangleIcon",
							variant: "danger"
						}
					})
				})
		}

		const deleteFile = (fileId) => {
			Vue.swal({
				title: "¿Esta seguro(a) de eliminar el archivo?",
				text: "¡Si no lo esta, puede cancelar la accion!",
				icon: "warning",
				showCancelButton: true,
				confirmButtonText: "Si, eliminar!",
				cancelButtonText: "Cancelar",
				customClass: {
					confirmButton: "btn btn-primary",
					cancelButton: "btn btn-outline-danger ml-1"
				},
				showClass: {
					popup: "animate__animated animate__tada"
				},
				buttonsStyling: false
			}).then((result) => {
				if (result.value) {
					store
						.dispatch("app-upload-evidence/deleteEvidenceFile", {
							formId: form.value,
							fileId
						})
						.then((response) => {
							getFiles()

							toast({
								component: ToastificationContent,
								props: {
									title: response.data.message,
									icon: "CheckIcon",
									variant: "success"
								}
							})
						})
						.catch(() => {
							toast({
								component: ToastificationContent,
								props: {
									title: "Error al eliminar el archivo",
									icon: "AlertTriangleIcon",
									variant: "danger"
								}
							})
						})
				}
			})
		}

		const isExcelAllowed = (index) => {
			return [2, 3].includes(evidenceType.value) && index === 1
		}

		const getAllowedAccept = (index) => {
			return isExcelAllowed(index) ? ".xls,.xlsx" : ".pdf"
		}

		const onFileSelected = (event, itemKey, index) => {
			const file = event.target.files[0]
			if (!file) return

			// Validate empty file
			if (file.size === 0) {
				toast({
					component: ToastificationContent,
					props: {
						title: "El archivo está vacío. Debe cargar un archivo válido.",
						icon: "AlertTriangleIcon",
						variant: "danger"
					}
				})
				event.target.value = ""
				return
			}

			// Validate extension
			const ext = file.name.split(".").pop().toLowerCase()
			const allowed = isExcelAllowed(index) ? ["xls", "xlsx"] : ["pdf"]

			if (!allowed.includes(ext)) {
				const msg = isExcelAllowed(index)
					? "Solo se permiten archivos Excel (.xls, .xlsx)."
					: "Solo se permiten archivos PDF (.pdf)."
				toast({
					component: ToastificationContent,
					props: {
						title: msg,
						icon: "AlertTriangleIcon",
						variant: "warning"
					}
				})
				event.target.value = ""
				return
			}

			uploadFile(itemKey, file, event)
		}

		const uploadFile = (itemKey, file, event) => {
			Vue.set(uploading.value, itemKey, true)

			const formData = new FormData()
			formData.append("file", file)
			formData.append("item_key", itemKey)

			store
				.dispatch("app-upload-evidence/uploadEvidenceFile", {
					formId: form.value,
					payload: formData
				})
				.then((response) => {
					getFiles()

					toast({
						component: ToastificationContent,
						props: {
							title: response.data.message,
							icon: "CheckIcon",
							variant: "success"
						}
					})
				})
				.catch((err) => {
					let message =
						err.response && err.response.data && err.response.data.message
							? err.response.data.message
							: "Error al cargar el archivo."

					toast({
						component: ToastificationContent,
						props: {
							title: message,
							icon: "AlertTriangleIcon",
							variant: "danger"
						}
					})
				})
				.finally(() => {
					Vue.set(uploading.value, itemKey, false)
					if (event && event.target) event.target.value = ""
				})
		}

		const submitEvidence = () => {
			if (!evidenceType.value) {
				toast({
					component: ToastificationContent,
					props: {
						title: "Seleccione el tipo de solicitud.",
						icon: "AlertTriangleIcon",
						variant: "warning"
					}
				})
				return
			}

			if (!requestedAmount.value || parseFloat(requestedAmount.value) <= 0) {
				toast({
					component: ToastificationContent,
					props: {
						title: "Debe ingresar el importe a solicitar.",
						icon: "AlertTriangleIcon",
						variant: "warning"
					}
				})
				return
			}

			// Check all required items (visible to this user) have files
			const items = currentItems.value || []
			const missingItems = items.filter(
				(item) => item.requires_file && !getFileForItem(item.key)
			)

			if (missingItems.length > 0) {
				toast({
					component: ToastificationContent,
					props: {
						title: "Todos los archivos son requeridos.",
						icon: "AlertTriangleIcon",
						variant: "warning"
					}
				})
				return
			}

			Vue.swal({
				title: "¿Esta seguro de enviar la solicitud?",
				text: "Una vez enviada no podrá modificarla.",
				icon: "warning",
				showCancelButton: true,
				confirmButtonText: "Si, enviar!",
				cancelButtonText: "Cancelar",
				customClass: {
					confirmButton: "btn btn-primary",
					cancelButton: "btn btn-outline-danger ml-1"
				},
				showClass: {
					popup: "animate__animated animate__tada"
				},
				buttonsStyling: false
			}).then((result) => {
				if (result.value) {
					submitting.value = true

					const payload = {
						evidence_type_id: evidenceType.value,
						evidence_requested_amount: parseFloat(requestedAmount.value)
					}

					store
						.dispatch("app-upload-evidence/submitEvidence", {
							formId: form.value,
							payload
						})
						.then((response) => {
							toast({
								component: ToastificationContent,
								props: {
									title: response.data.message,
									icon: "CheckIcon",
									variant: "success"
								}
							})

							modal.value.hide()
							emit("refetch-data")
						})
						.catch((err) => {
							let message =
								err.response && err.response.data && err.response.data.message
									? err.response.data.message
									: "Error al enviar la solicitud."

							toast({
								component: ToastificationContent,
								props: {
									title: message,
									icon: "AlertTriangleIcon",
									variant: "danger"
								}
							})
						})
						.finally(() => {
							submitting.value = false
						})
				}
			})
		}

		const onlyNumbers = (event) => {
			const charCode = event.which ? event.which : event.keyCode
			if ((charCode < 48 || charCode > 57) && charCode !== 46) {
				event.preventDefault()
			}
		}

		return {
			// DATA
			files,
			evidenceType,
			evidenceTypeOptions,
			computedEvidenceTypeOptions,
			evidenceTypeLabel,
			requestedAmount,
			requestedAmountFormatted,
			uploading,
			submitting,
			currentItems,
			totalCartasCount,

			// ROLE
			isProvider,
			isAdmin,
			canEdit,

			// STATE
			isCtsOrGratificacion,
			submitted,
			expired,
			countdownText,
			displayText,
			displayIcon,
			isMensualAllowed,
			isCtsAllowed,
			isGratificationAllowed,

			// VALIDATION
			fileValidations,
			evidenceComment,
			evidenceObservation,
			savingValidation,

			// BULK DOWNLOAD
			downloadingAll,
			hasUploadedFiles,
			downloadAllFiles,

			// REFS
			modal,

			// ACTIONS
			openModal,
			viewFile,
			deleteFile,
			submitEvidence,
			onlyNumbers,
			onFileSelected,
			getFileForItem,
			getAllowedAccept,
			onEvidenceTypeChange,
			onModalHidden,
			toggleValidation,
			saveValidation
		}
	}
}
</script>

<style lang="scss" scoped>
.description-modal {
	font-size: 1.2rem;
	font-weight: bold;
}

.content-modal {
	font-size: 0.995rem;
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-sweetalert.scss";
</style>
